import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Info, Calendar, AlertCircle, CheckCircle, RefreshCw, Server } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
interface PayoutStats {
  pending_amount: number;
  scheduled_amount: number;
  completed_amount: number;
  next_payout_date: string | null;
}
interface PayoutTransaction {
  id: string;
  amount: number;
  chef_payout: number;
  payout_status: 'pending' | 'scheduled' | 'completed';
  payout_scheduled_date: string | null;
  stripe_transfer_id: string | null;
  created_at: string;
  status: string;
  meal: {
    name: string;
  } | null;
  order_id: string | null;
}
interface PayoutSettings {
  payout_delay_days: number;
}
interface CronJobMetric {
  id: string;
  metric_name: string;
  metric_value: number;
  timestamp: string;
  metadata: {
    job_name: string;
    success: boolean;
    error?: string;
  };
}
export function PayoutsSection({
  formatNumber
}: {
  formatNumber: (num: number) => string;
}) {
  const {
    toast
  } = useToast();
  const [isRefetching, setIsRefetching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showDebugDialog, setShowDebugDialog] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState<any>(null);
  const [operationResult, setOperationResult] = useState<string | null>(null);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isServiceError, setIsServiceError] = useState(false);
  const {
    data: payoutData,
    isLoading: isQueryLoading,
    error: queryError,
    refetch
  } = useQuery({
    queryKey: ['chef-payouts', retryCount],
    queryFn: async () => {
      const {
        data: userData
      } = await supabase.auth.getUser();
      if (!userData?.user) {
        throw new Error('Not authenticated');
      }
      try {
        console.log("Calling check-payouts function for user", userData.user.id);
        const {
          data,
          error
        } = await supabase.functions.invoke('check-payouts', {
          body: {
            userId: userData.user.id
          }
        });
        if (error) {
          console.error("Error from check-payouts:", error);
          setIsServiceError(true);
          throw error;
        }
        if (!data.success) {
          console.error("Function returned error:", data.error);
          setIsServiceError(true);
          throw new Error(data.error || 'Failed to fetch payout data');
        }
        if (data.stats) {
          const stats: PayoutStats = {
            pending_amount: typeof data.stats.pending_amount === 'number' ? data.stats.pending_amount : 0,
            scheduled_amount: typeof data.stats.scheduled_amount === 'number' ? data.stats.scheduled_amount : 0,
            completed_amount: typeof data.stats.completed_amount === 'number' ? data.stats.completed_amount : 0,
            next_payout_date: data.stats.next_payout_date
          };
          data.stats = stats;
        } else {
          data.stats = {
            pending_amount: 0,
            scheduled_amount: 0,
            completed_amount: 0,
            next_payout_date: null
          };
        }
        setIsServiceError(false);
        console.log('Payout data:', data);
        return data;
      } catch (err) {
        console.error('Error fetching payout data:', err);
        setLastError(err instanceof Error ? err.message : String(err));
        throw err;
      }
    },
    retry: 1
  });
  useEffect(() => {
    if (queryError) {
      setLastError(queryError instanceof Error ? queryError.message : String(queryError));
    }
  }, [queryError]);
  const handleRefresh = async () => {
    setIsRefetching(true);
    setLastError(null);
    try {
      await refetch();
      toast({
        title: "Refreshed",
        description: "Payout data has been refreshed"
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to refresh payout data",
        variant: "destructive"
      });
    } finally {
      setIsRefetching(false);
    }
  };
  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setIsRefetching(true);
    setLastError(null);
    refetch().then(() => {
      toast({
        title: "Retry Successful",
        description: "Payout data has been refreshed"
      });
    }).catch(err => {
      toast({
        title: "Retry Failed",
        description: "Could not update payout status",
        variant: "destructive"
      });
    }).finally(() => {
      setIsRefetching(false);
    });
  };
  const fetchTransactionDetails = async (transactionId: string) => {
    try {
      const {
        data,
        error
      } = await supabase.from('transactions').select('*').eq('id', transactionId).single();
      if (error) throw error;
      setTransactionDetails(data);
      setShowTransactionDialog(true);
    } catch (err) {
      console.error('Error fetching transaction details:', err);
      toast({
        title: "Error",
        description: "Could not fetch transaction details",
        variant: "destructive"
      });
    }
  };
  const triggerPayoutFunction = async (functionName: string, options: any = {}) => {
    setIsLoading(true);
    setOperationResult(null);
    setLastError(null);
    try {
      const {
        data: userData
      } = await supabase.auth.getUser();
      if (!userData?.user) {
        throw new Error('Not authenticated');
      }
      const payload = {
        manual: true,
        dryRun: options.dryRun || false,
        chefId: userData.user.id,
        ...options
      };
      console.log(`Triggering ${functionName} with payload:`, payload);
      const {
        data,
        error
      } = await supabase.functions.invoke('check-payouts', {
        body: {
          userId: userData.user.id,
          manualTrigger: {
            functionName,
            payload
          }
        }
      });
      if (error) {
        console.error(`Error invoking check-payouts:`, error);
        throw error;
      }
      if (!data.success) {
        console.error(`Error from check-payouts:`, data.error);
        throw new Error(data.error || `Failed to trigger ${functionName}`);
      }
      console.log(`${functionName} result:`, data);
      setOperationResult(JSON.stringify(data.triggerResult, null, 2));
      toast({
        title: "Success",
        description: `Successfully triggered ${functionName}`
      });
      await refetch();
    } catch (err) {
      console.error(`Error triggering ${functionName}:`, err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      setLastError(errorMessage);
      setOperationResult(`Error: ${errorMessage}`);
      toast({
        title: "Error",
        description: `Failed to trigger ${functionName}: ${errorMessage}`,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const stats: PayoutStats = payoutData?.stats || {
    pending_amount: 0,
    scheduled_amount: 0,
    completed_amount: 0,
    next_payout_date: null
  };
  const recentPayouts: PayoutTransaction[] = payoutData?.recentPayouts || [];
  const settings = payoutData?.payoutSettings || {
    payout_delay_days: 3
  };
  const cronJobs = payoutData?.cronJobs || [];
  const serviceRoleKeyConfigured = payoutData?.serviceRoleKeyConfigured || false;
  const transactionCount = payoutData?.transactionCount || 0;
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not scheduled';
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (err) {
      return 'Invalid date';
    }
  };
  const getStatusBadge = (status: string, transactionStatus: string) => {
    if (transactionStatus !== 'completed') {
      return <Badge variant="outline" className="bg-amber-100">{transactionStatus}</Badge>;
    }
    switch (status) {
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      case 'scheduled':
        return <Badge variant="secondary">Scheduled</Badge>;
      case 'completed':
        return <Badge variant="default" className="bg-green-500 hover:bg-green-600">Paid</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };
  const renderPayoutStats = () => <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Pending</CardTitle>
        </CardHeader>
        <CardContent>
          {isQueryLoading ? <Skeleton className="h-8 w-24" /> : <p className="text-2xl font-bold">${formatNumber(stats.pending_amount)}</p>}
          <p className="text-xs text-muted-foreground">Not yet scheduled for payout</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Scheduled</CardTitle>
        </CardHeader>
        <CardContent>
          {isQueryLoading ? <Skeleton className="h-8 w-24" /> : <p className="text-2xl font-bold">${formatNumber(stats.scheduled_amount)}</p>}
          <p className="text-xs text-muted-foreground">
            {stats.next_payout_date ? `Next payout: ${formatDate(stats.next_payout_date)}` : 'No payouts scheduled'}
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Total Paid</CardTitle>
        </CardHeader>
        <CardContent>
          {isQueryLoading ? <Skeleton className="h-8 w-24" /> : <p className="text-2xl font-bold">${formatNumber(stats.completed_amount)}</p>}
          <p className="text-xs text-muted-foreground">All time</p>
        </CardContent>
      </Card>
    </div>;
  const renderErrorState = () => <Card className="p-6 bg-red-50/30 border-red-200 mb-6">
      <div className="flex flex-col items-center justify-center text-center space-y-4">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <div className="space-y-2">
          <h3 className="font-semibold text-lg">Error Loading Payout Data</h3>
          <p className="text-sm text-muted-foreground">
            There was an error loading your payout information. This might be due to a temporary system issue.
          </p>
          {lastError && <div className="p-2 bg-red-50 rounded text-xs text-red-700 mt-2">
              {lastError}
            </div>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRetry} disabled={isRefetching}>
            {isRefetching ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            {isRefetching ? 'Retrying...' : 'Retry'}
          </Button>
          <Button variant="default" onClick={() => setShowDebugDialog(true)}>
            <Server className="h-4 w-4 mr-2" />
            Troubleshoot
          </Button>
        </div>
      </div>
    </Card>;
  const renderRecentPayouts = () => <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-lg">Recent Payouts</CardTitle>
            <CardDescription>
              Your recent and upcoming payouts
            </CardDescription>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setShowDebugDialog(true)}>
                  <Info className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Payout information and settings</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent>
        {isQueryLoading ? <div className="space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
          </div> : recentPayouts.length > 0 ? <Table>
            <TableCaption>
              <div className="flex flex-col gap-2 items-center">
                <button onClick={handleRefresh} disabled={isRefetching} className="text-sm text-primary flex items-center">
                  {isRefetching && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                  Refresh payout data
                </button>
                <div className="text-xs text-muted-foreground">
                  {transactionCount} total completed transactions
                </div>
              </div>
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Meal</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentPayouts.map(payout => <TableRow key={payout.id} className="cursor-pointer hover:bg-muted/50" onClick={() => fetchTransactionDetails(payout.id)}>
                  <TableCell className="font-medium">
                    {formatDate(payout.payout_scheduled_date || payout.created_at)}
                  </TableCell>
                  <TableCell className="truncate max-w-[120px]">
                    {payout.meal?.name || 'Unknown meal'}
                  </TableCell>
                  <TableCell>${formatNumber(payout.chef_payout)}</TableCell>
                  <TableCell>{getStatusBadge(payout.payout_status, payout.status)}</TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <Info className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table> : <div className="text-center py-6 text-muted-foreground flex flex-col items-center gap-2">
            <p>No payout history yet</p>
            <div className="text-xs text-muted-foreground">
              {transactionCount === 0 ? "You don't have any completed transactions to process" : `${transactionCount} completed transactions found but none are ready for payout`}
            </div>
          </div>}
      </CardContent>
      <CardFooter className="justify-between items-center">
        <div className="text-xs text-muted-foreground">
          {!serviceRoleKeyConfigured && <div className="flex items-center text-amber-600">
              <AlertCircle className="h-3.5 w-3.5 mr-1" />
              Service role key not configured
            </div>}
          {lastError && !isServiceError && <div className="flex items-center text-red-600">
              <AlertCircle className="h-3.5 w-3.5 mr-1" />
              {lastError}
            </div>}
        </div>
        <div className="flex gap-2">
          
          
        </div>
      </CardFooter>
      
      <Dialog open={showDebugDialog} onOpenChange={setShowDebugDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Payout Information</DialogTitle>
            <DialogDescription>
              System configuration and status for payouts
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {lastError && <div className="rounded-md bg-red-50 p-3 text-red-700 text-sm">
                <p className="font-semibold">Error:</p>
                <p>{lastError}</p>
              </div>}
            
            <div className="space-y-2">
              <h4 className="font-medium">Payout Settings</h4>
              <div className="rounded-md bg-muted p-3 text-sm">
                <p>Payout delay: {settings.payout_delay_days} days</p>
                <p>Service role key: {serviceRoleKeyConfigured ? 'Configured' : 'Not configured'}</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium">Recent Cron Jobs</h4>
              {cronJobs.length > 0 ? <div className="text-sm space-y-2">
                  {cronJobs.map(job => <div key={job.id} className={`rounded-md p-3 text-xs ${job.metadata.success ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className="flex items-center">
                        {job.metadata.success ? <Badge variant="outline" className="bg-green-100">Success</Badge> : <Badge variant="outline" className="bg-red-100">Failed</Badge>}
                        <span className="ml-2">{job.metadata.job_name}</span>
                      </div>
                      <div className="mt-1 text-muted-foreground">
                        {new Date(job.timestamp).toLocaleString()}
                      </div>
                      {job.metadata.error && <div className="mt-1 text-red-500">
                          Error: {job.metadata.error}
                        </div>}
                    </div>)}
                </div> : <div className="rounded-md bg-muted p-3 text-sm flex items-center">
                  <AlertCircle className="h-4 w-4 mr-2 text-yellow-500" />
                  No cron job records found
                </div>}
            </div>
            
            {operationResult && <div className="space-y-2">
                <h4 className="font-medium">Operation Result</h4>
                <pre className="rounded-md bg-muted p-3 text-xs overflow-auto max-h-40">
                  {operationResult}
                </pre>
              </div>}

            <div className="space-y-2">
              <h4 className="font-medium">Advanced Options</h4>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => triggerPayoutFunction('schedule-payouts', {
                dryRun: true
              })}>
                  Schedule (Dry Run)
                </Button>
                <Button size="sm" variant="outline" onClick={() => triggerPayoutFunction('process-scheduled-payouts', {
                dryRun: true
              })}>
                  Process (Dry Run)
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowDebugDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <Dialog open={showTransactionDialog} onOpenChange={setShowTransactionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
            <DialogDescription>
              Detailed information about this transaction
            </DialogDescription>
          </DialogHeader>
          
          {transactionDetails ? <div className="space-y-4 py-4">
              <div className="rounded-md bg-muted p-3 text-sm">
                <p><strong>ID:</strong> {transactionDetails.id}</p>
                <p><strong>Amount:</strong> ${formatNumber(transactionDetails.chef_payout)}</p>
                <p><strong>Status:</strong> {transactionDetails.status}</p>
                <p><strong>Payout Status:</strong> {transactionDetails.payout_status}</p>
                <p><strong>Created At:</strong> {new Date(transactionDetails.created_at).toLocaleString()}</p>
                <p><strong>Scheduled Date:</strong> {transactionDetails.payout_scheduled_date ? new Date(transactionDetails.payout_scheduled_date).toLocaleString() : 'Not scheduled'}</p>
                <p><strong>Transfer ID:</strong> {transactionDetails.stripe_transfer_id || 'Not processed'}</p>
              </div>
            </div> : <div className="py-4">
              <Skeleton className="h-40 w-full" />
            </div>}
          
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowTransactionDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>;
  return <div className="space-y-4">
      {isServiceError && renderErrorState()}
      {renderPayoutStats()}
      {renderRecentPayouts()}
    </div>;
}